import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { fetchData, fetchPostData } from '@/Components/hooks/Api';
import { useAuth } from '@/Context/Auth/AuthContext';
import { useIncident } from '@/Context/Incident/IncidentContext';
import { useArrest } from '@/Context/Arrest/ArrestContext';
import { useName } from '@/Context/Name/NameContext';
import { useOffense } from '@/Context/Offense/OffenseContext';
import { useProperty } from '@/Context/Property/PropertyContext';
import { useVehicle } from '@/Context/Vehicle/VehicleContext';
import { useWarrant } from '@/Context/Warrant/WarrantContext';
import { useSearch } from '@/Context/Search/SearchContext';
import { useNIBRS } from '@/Context/NIBRS/NIBRSContext';
import { useCaseManagement } from '@/Context/CaseManagement/CaseManagementContext';

export const AgencyContext = createContext();

const AgencyData = ({ children }) => {
  // Agency-only state kept local.
  const [agencyData, setAgencyData] = useState([]);
  const [agencyFilterData, setAgencyFilterData] = useState([]);
  const [editStatus, setEditStatus] = useState(false);
  const [count, setCount] = useState({});
  const [agencyID, setAgencyID] = useState('');
  const [agnecyName, setAgencyName] = useState('');
  const [status, setStatus] = useState(false);
  const [showPage, setShowPage] = useState('home');
  const [changesStatus, setChangesStatus] = useState(false);
  const [changesStatusCount, setChangesStatusCount] = useState(0);
  const [inActiveStatus, setInActiveStatus] = useState(false);
  const [agencySingleData, setAgencySingleData] = useState([]);
  const [isCadEditable, setIsCadEditable] = useState(false);
  const [showWatermark, setShowWatermark] = useState(false);
  const [sealUnsealPage, setSealUnsealPage] = useState('seal');
  const [updateCount, setUpdateCount] = useState(0);
  const [utilityTable, setUtilityTable] = useState({});

  // Personnel state remains in AgencyContext for now.
  const [personnelList, setPersonnelList] = useState([]);
  const [personnelFilterData, setPersonnelFilterData] = useState('');
  const [showPagePersonnel, setShowPagePersonnel] = useState('home');
  const [personnelStatus, setPersonnelStatus] = useState(false);
  const [PersonnelEffectiveScreenPermission, setPersonnelEffectiveScreenPermission] = useState();
  const [personnelData, setPersonnelData] = useState([]);

  const agencySingleFetchRef = useRef({ agencyId: null, inFlight: false, promise: null });

  const auth = useAuth();
  const incident = useIncident();
  const arrest = useArrest();
  const name = useName();
  const offense = useOffense();
  const property = useProperty();
  const vehicle = useVehicle();
  const warrant = useWarrant();
  const search = useSearch();
  const nibrs = useNIBRS();
  const caseManagement = useCaseManagement();

  const getAgency = (AgencyID, PinID) => {
    fetchPostData('Agency/GetData_Agency', { AgencyID, PINID: PinID }).then((data) => {
      setAgencyData(data || []);
      setAgencyFilterData(data || []);
    });
  };

  const getInActiveAgency = () => {
    fetchData('Agency/GetData_InActiveAgency').then((data) => {
      setAgencyData(data || []);
      setAgencyFilterData(data || []);
    });
  };

  const GetAgencySingleData = (AgencyID, force = false) => {
    if (!AgencyID && AgencyID !== 0) return Promise.resolve([]);
    const agencyKey = String(AgencyID);
    const isSame = agencySingleFetchRef.current.agencyId === agencyKey;
    const hasData = isSame && agencySingleData?.length > 0;
    if (!force && isSame && hasData) return Promise.resolve(agencySingleData);
    if (!force && isSame && agencySingleFetchRef.current.inFlight) {
      return agencySingleFetchRef.current.promise;
    }

    const promise = fetchPostData('Agency/GetData_SingleData', { AgencyID })
      .then((res) => {
        const data = res?.length > 0 ? res : [];
        setAgencySingleData(data);
        setIsCadEditable(data?.[0]?.IsCadEditable || false);
        return data;
      })
      .catch(() => {
        setAgencySingleData([]);
        setIsCadEditable(false);
        return [];
      })
      .finally(() => {
        agencySingleFetchRef.current = { agencyId: agencyKey, inFlight: false, promise: null };
      });

    agencySingleFetchRef.current = { agencyId: agencyKey, inFlight: true, promise };
    return promise;
  };

  const get_CountList = (AgencyID, PINID) => {
    fetchPostData('HomeApi/GetData_AgencyCount', { AgencyID, PINID: PINID || 0 }).then((res) => {
      setCount(res?.[0] || {});
    });
  };

  const get_Personnel_Lists = (id, PINID) => {
    fetchPostData('Personnel/GetData_Personnel', { AgencyID: id, PINID }).then((res) => {
      setPersonnelList(res || []);
      setPersonnelFilterData(res || []);
    });
  };

  const getInActive_Personnel = (id) => {
    fetchPostData('Personnel/GetData_InActivePersonnel', { AgencyID: id }).then((res) => {
      setPersonnelList(res || []);
      setPersonnelFilterData(res || []);
    });
  };

  const getPersonnelList = () => {
    fetchData('CADIncidentStatus/GetData_CurrentStatus').then((res) => {
      setPersonnelData(res?.length > 0 ? res : []);
    });
  };

  // Preserve legacy behavior where these methods updated Incident tabCount.
  const get_ArrestCharge_Count = (ChargeID) => {
    fetchPostData('ArrestCharge/GetData_ArrestChargeCount', { ChargeID }).then((res) => {
      incident.setTabCount(res?.[0] || []);
    });
  };

  const get_Warrent_Count = (WarrantID) => {
    fetchPostData('HomeApi/GetData_WarrantCount', { WarrantID }).then((res) => {
      incident.setTabCount(res?.[0] || []);
    });
  };

  const get_NameOffender_Count = (NameID) => {
    fetchPostData('NameVictimCount/GetData_NameOffenderCount', { NameID }).then((res) => {
      incident.setTabCount(res?.[0] || []);
    });
  };

  const get_MissingPerson_Count = (MissingPersonID, loginPinID) => {
    fetchPostData('MissingPerson/GetData_MissingPersonCount', { MissingPersonID, PINID: loginPinID }).then((res) => {
      incident.setTabCount(res?.[0] || []);
    });
  };

  const value = useMemo(
    () => ({
      ...auth,
      ...incident,
      ...arrest,
      ...name,
      ...offense,
      ...property,
      ...vehicle,
      ...warrant,
      ...search,
      ...nibrs,
      ...caseManagement,

      // Agency + personnel compatibility surface.
      agencyData, agencyFilterData, setAgencyFilterData,
      editStatus, setEditStatus,
      count, setCount,
      agencyID, setAgencyID,
      agnecyName, setAgencyName,
      status, setStatus,
      showPage, setShowPage,
      changesStatus, setChangesStatus,
      changesStatusCount, setChangesStatusCount,
      inActiveStatus, setInActiveStatus,
      agencySingleData,
      isCadEditable,
      showWatermark, setShowWatermark,
      sealUnsealPage, setSealUnsealPage,
      updateCount, setUpdateCount,
      utilityTable, setUtilityTable,

      personnelList, setPersonnelList,
      personnelFilterData, setPersonnelFilterData,
      showPagePersonnel, setShowPagePersonnel,
      personnelStatus, setPersonnelStatus,
      PersonnelEffectiveScreenPermission, setPersonnelEffectiveScreenPermission,
      personnelData,

      getAgency, getInActiveAgency, GetAgencySingleData, get_CountList,
      get_Personnel_Lists, getInActive_Personnel, getPersonnelList,

      // Backward-compatible aliases/wrappers.
      setshowOffPage: offense.setOffenceShowPage,
      get_ArrestCharge_Count,
      get_Warrent_Count,
      get_NameOffender_Count,
      get_MissingPerson_Count,
    }),
    [
      auth, incident, arrest, name, offense, property, vehicle, warrant, search, nibrs, caseManagement,
      agencyData, agencyFilterData, editStatus, count, agencyID, agnecyName, status, showPage, changesStatus, changesStatusCount,
      inActiveStatus, agencySingleData, isCadEditable, showWatermark, sealUnsealPage, updateCount, utilityTable,
      personnelList, personnelFilterData, showPagePersonnel, personnelStatus, PersonnelEffectiveScreenPermission, personnelData,
    ]
  );

  return <AgencyContext.Provider value={value}>{children}</AgencyContext.Provider>;
};

export const useAgency = () => {
  const context = useContext(AgencyContext);
  if (!context) throw new Error('useAgency must be used within AgencyData provider');
  return context;
};

export default AgencyData;